import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { PageFade, Reveal } from "@/components/site/motion";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

function GalleryItem({ item, index, uniform }) {
  const orientation = item.orientation || ["landscape", "portrait", "square"][index % 3];
  const isOriginal = orientation === "original";
  const isVideo = item.type === "video";
  const span = uniform
    ? isOriginal
      ? "col-span-full"
      : orientation === "wide"
      ? "col-span-full aspect-[21/9]"
      : isVideo
      ? "col-span-1 relative aspect-[9/16]"
      : orientation === "portrait"
      ? "col-span-1 aspect-[4/5]"
      : orientation === "square"
      ? "col-span-1 aspect-square"
      : "col-span-1 aspect-[16/10]"
    : isVideo
    ? "col-span-12 sm:col-span-6 lg:col-span-4 relative aspect-[9/16]"
    : isOriginal
    ? "col-span-12"
    : orientation === "wide"
    ? "col-span-12 aspect-[21/9]"
    : orientation === "portrait"
    ? "col-span-12 sm:col-span-6 lg:col-span-5 aspect-[4/5]"
    : orientation === "square"
    ? "col-span-12 sm:col-span-6 lg:col-span-6 aspect-square"
    : orientation === "landscape" && index % 3 === 0
    ? "col-span-12 aspect-[16/8]"
    : "col-span-12 sm:col-span-6 lg:col-span-7 aspect-[16/10]";
  const mediaClass = isVideo
    ? "absolute inset-0 h-full w-full rounded-2xl border border-white/10 object-cover"
    : isOriginal
    ? "h-auto w-full rounded-2xl border border-white/10 object-cover"
    : "h-full w-full rounded-2xl border border-white/10 object-cover";

  return (
    <Reveal delay={(index % 3) * 0.08} className={span}>
      {item.type === "video" ? (
        <video
          src={mediaUrl(item.url) + "#t=0.1"}
          controls
          muted
          playsInline
          preload="metadata"
          className={mediaClass}
          data-testid={`gallery-video-${index}`}
        />
      ) : (
        <img
          src={mediaUrl(item.url)}
          alt={item.alt || `Gallery image ${index + 1}`}
          loading="lazy"
          className={mediaClass}
          data-testid={`gallery-image-${index}`}
        />
      )}
    </Reveal>
  );
}

export default function ProjectDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [next, setNext] = useState(null);
  const [status, setStatus] = useState("loading");
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get(`/testimonials?context=portfolio&project=${slug}`).then((r) => setTestimonials(r.data || [])).catch(() => {});
  }, [slug]);
  useDocumentTitle(project ? `${project.title} — JARE` : "Project — JARE");

  useEffect(() => {
    setStatus("loading");
    setProject(null);
    api
      .get(`/portfolio/${slug}`)
      .then((r) => {
        setProject(r.data);
        setStatus("ok");
        api.get("/portfolio").then((list) => {
          const idx = list.data.findIndex((p) => p.slug === slug);
          if (idx >= 0 && list.data.length > 1) {
            setNext(list.data[(idx + 1) % list.data.length]);
          }
        });
      })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  if (status === "loading") {
    return (
      <div className="mx-auto w-[92%] max-w-7xl pt-48 pb-32">
        <div className="aspect-[16/6] animate-pulse rounded-3xl bg-white/[0.04]" />
      </div>
    );
  }

  if (status === "notfound") {
    return (
      <PageFade className="mx-auto flex min-h-[70vh] w-[92%] max-w-7xl flex-col items-start justify-center">
        <p className="eyebrow mb-6">404</p>
        <h1 className="font-display text-4xl font-bold text-white" data-testid="project-not-found">
          This project doesn&apos;t exist (yet).
        </h1>
        <Link to="/work" className="btn-ghost mt-8" data-testid="back-to-work-link">
          <ArrowLeft className="h-4 w-4" /> Back to work
        </Link>
      </PageFade>
    );
  }

  const meta = [
    ["Client", project.client],
    ["Year", project.year],
    ["Role", project.role],
    ["Tools", (project.tools || []).join(", ")],
  ].filter(([, v]) => v);

  return (
    <PageFade className="mx-auto w-[92%] max-w-7xl pt-36 pb-28">
      <Link
        to="/work"
        data-testid="project-back-link"
        className="group inline-flex items-center gap-2 text-sm text-neutral-400 transition-colors hover:text-white"
      >
        <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" /> All work
      </Link>

      <header className="mt-10">
        <Reveal>
          <p className="eyebrow mb-5" data-testid="project-category">
            {project.category} · {project.year}
          </p>
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-7xl" data-testid="project-title">
            {project.title}
          </h1>
          {project.subtitle && (
            <p className="mt-5 max-w-2xl text-lg text-neutral-400 md:text-xl" data-testid="project-subtitle">
              {project.subtitle}
            </p>
          )}
        </Reveal>

        {meta.length > 0 && (
          <Reveal delay={0.1}>
            <div className="glass mt-12 grid grid-cols-2 gap-px overflow-hidden rounded-2xl md:grid-cols-4" data-testid="project-meta">
              {meta.map(([k, v]) => (
                <div key={k} className="bg-white/[0.015] p-5">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">{k}</p>
                  <p className="mt-2 text-sm font-medium text-white">{v}</p>
                </div>
              ))}
            </div>
          </Reveal>
        )}
      </header>

      {project.cover_media?.url && project.show_cover !== false && (
        <Reveal delay={0.15} className="mt-14">
          {project.cover_media.type === "video" ? (
            <video
              src={mediaUrl(project.cover_media.url) + "#t=0.1"}
              controls
              autoPlay
              muted
              loop
              playsInline
              className="aspect-video w-full rounded-3xl border border-white/10 object-cover"
              data-testid="project-cover-video"
            />
          ) : (
            <img
              src={mediaUrl(project.cover_media.url)}
              alt={project.cover_media.alt || project.title}
              className="max-h-[80vh] w-full rounded-3xl border border-white/10 object-cover"
              data-testid="project-cover-image"
            />
          )}
        </Reveal>
      )}

      {project.body && (
        <Reveal className="mx-auto mt-16 max-w-3xl">
          <div className="rich-text" dangerouslySetInnerHTML={{ __html: project.body }} data-testid="project-body" />
        </Reveal>
      )}

      {project.gallery?.length > 0 && (() => {
        const gl = project.gallery_layout;
        const cD = gl?.columns_desktop || 0;
        const cT = gl?.columns_tablet || 0;
        const cM = gl?.columns_mobile || 0;
        const custom = Boolean(cD || cT || cM);
        const GAP = { tight: "gap-3 md:gap-4", normal: "gap-5 md:gap-7", loose: "gap-8 md:gap-12" }[gl?.gap] || "gap-5 md:gap-7";
        const COLS = { 1: "grid-cols-1", 2: "grid-cols-2", 3: "grid-cols-3" };
        const SM = { 1: "sm:grid-cols-1", 2: "sm:grid-cols-2", 3: "sm:grid-cols-3" };
        const LG = { 1: "lg:grid-cols-1", 2: "lg:grid-cols-2", 3: "lg:grid-cols-3" };
        const effM = cM || 1;
        const effT = cT || (cD ? Math.min(cD, 2) : effM);
        const effD = cD || effT;
        const gridCls = custom ? `grid ${COLS[effM]} ${SM[effT]} ${LG[effD]} ${GAP}` : `grid grid-cols-12 ${GAP}`;
        const wrapCls =
          gl?.width === "full"
            ? "relative left-1/2 mt-20 w-screen max-w-none -translate-x-1/2 px-3 md:px-5"
            : gl?.width === "wide"
            ? "relative left-1/2 mt-20 w-[94vw] max-w-[1500px] -translate-x-1/2"
            : "mt-20";
        return (
          <div className={wrapCls}>
            <div className={gridCls} data-testid="project-gallery">
              {project.gallery.map((item, i) => (
                <GalleryItem key={i} item={item} index={i} uniform={custom} />
              ))}
            </div>
          </div>
        );
      })()}

      {testimonials.length > 0 && (
        <section className="mt-20" data-testid="project-testimonials">
          <Reveal>
            <p className="eyebrow mb-6">Client words</p>
          </Reveal>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {testimonials.slice(0, 2).map((t, i) => (
              <Reveal key={t.id || t.name} delay={i * 0.08}>
                <figure className="glass glass-hover h-full rounded-3xl p-8">
                  <blockquote className="text-base leading-relaxed text-neutral-300">“{t.text}”</blockquote>
                  <figcaption className="mt-6 flex items-center gap-3 border-t border-white/[0.08] pt-5">
                    {t.photo ? (
                      <img src={mediaUrl(t.photo)} alt={t.name} className="h-10 w-10 rounded-xl border border-white/10 object-cover" />
                    ) : (
                      <span className="glass flex h-10 w-10 shrink-0 items-center justify-center rounded-xl font-display text-sm font-semibold text-white">
                        {(t.name || "?")[0]}
                      </span>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-white">{t.name}</p>
                      <p className="mt-0.5 truncate text-xs text-neutral-500">{t.position}</p>
                    </div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {next && (
        <Link to={`/work/${next.slug}`} className="group mt-24 block" data-testid="next-project-link">
          <div className="glass glass-hover rounded-3xl p-8 md:p-12">
            <p className="eyebrow mb-4">Next project</p>
            <div className="flex items-center justify-between gap-6">
              <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
                {next.title}
              </h2>
              <ArrowRight className="h-8 w-8 shrink-0 text-neutral-500 transition-all duration-300 group-hover:translate-x-2 group-hover:text-white" />
            </div>
          </div>
        </Link>
      )}
    </PageFade>
  );
}
